<template>
  <div>
    <CalicoDashboard
      class="search-results"
      no-content-padding
    >
      <template #content>
        <div class="body">
          <div class="heading">
            <div>
              <CalicoChip text="Search Results" />
            </div>
            <label
              v-if="$route.query.deep"
              class="body--xsmall"
            >{{ pagination.total }} businesses that match {{
              $route.query.businessName }}</label>
            <label
              v-else-if="showGenericResultsLabel"
              class="body--xsmall"
            >{{ pagination.total }} businesses found</label>
          </div>

          <div
            v-if="loading"
            class="calico-loader"
          >
            <Loader />
          </div>
          <div
            v-for="(business, index) in businesses"
            :key="index"
            class="business-cards-container"
          >
            <CalicoBusinessCard 
              v-if="!loading" 
              :key="index" 
              v-model="selectedBusiness" 
              :value="business"
              :title="business.name" 
              :state="business.state" 
              :city="business.city" 
              :address="business.street"
              :zip="business.zip" 
              :description="business.description" 
              :status-badge="betascores[index]"
              @click="openBusiness(business)"
            />
          </div>
          <CalicoPagination
            v-if="!loading && pagination.pages"
            :pages="pagination.pages"
            :label="paginationLabel"
          />
          <DeepSearcBanner v-if="!loading" />
        </div>
      </template>
    </CalicoDashboard>
    <UsageViewProfileConfirmation 
      v-if="confirmOpen" 
      :limit="limit" 
      :remaining="remaining" 
      :viewed="viewed"
      :confirming="confirming" 
      :error-message="usageErrorMessage" 
      @close="confirmOpen = false"
      @confirm="onConfirmProceed" 
    />
    <Modal
      v-if="showErrorModal"
      header="View Business"
      @close="showErrorModal = false"
    >
      <template #content>
        <CalicoConfirmation
          error="Unable to View business"
          message="This will not count towards your usage."
        />
      </template>
      <template #footer>
        <button
          class="button-secondary"
          @click="showErrorModal = false"
        >
          Go Back
        </button>
      </template>
    </Modal>
  </div>
</template>


<script>
import CalicoChip from '@/components/calico/chip/index.vue'
import CalicoDashboard from "@/components/template/dashboard/index.vue"
import CalicoBusinessCard from "@/components/calico/businessCard/index.vue"
import axios from '@/axios.js'
import Loader from '@/components/calico/loader/index.vue'
import CalicoPagination from '@/components/calico/pagination/index.vue'
import UsageViewProfileConfirmation from '@/components/pattern/usageViewProfileConfirmation/index.vue'
import { useUsage, deriveUsageMetrics } from "@/composables/auth/useUsage"
import Modal from '@/components/template/modal/index.vue'
import services from '@/services'
import DeepSearcBanner from "@/components/pattern/deepSearchBanner"
import CalicoConfirmation from "@/components/pattern/confirmation"

export default {
  name: 'SearchResultsPage',
  components: {
    CalicoChip,
    CalicoBusinessCard,
    CalicoDashboard,
    Loader,
    CalicoPagination,
    UsageViewProfileConfirmation,
    Modal,
    DeepSearcBanner,
    CalicoConfirmation
  },
  setup() {
    const { data, loading, errorMessage, fetchOrgUsage, fetchCurrentUsage } = useUsage()
    const { limit, remaining, viewed } = deriveUsageMetrics(data, loading)
    return {
      usageData: data,
      usageLoading: loading,
      usageErrorMessage: errorMessage,
      fetchOrgUsage,
      fetchCurrentUsage,
      limit,
      remaining,
      viewed
    }
  },
  data() {
    return {
      businesses: [],
      pagination: {
        page: 1,
        pages: 1,
        limit: 20,
        total: 0,
      },
      loading: false,
      confirmOpen: false,
      confirming: false,
      selectedBusiness: null,
      observer: null,
      lastQueryParams: null,
      betascores: [],
      showErrorModal: false
    }
  },
  computed: {
    paginationLabel() {
      const { page, limit, total } = this.pagination
      return `${(page * limit) - limit + 1}-${Math.min((page * limit), total)} of ${total}`
    },
    showGenericResultsLabel() {
      return false;
    }
  },
  watch: {
    '$route.query': {
      handler(newQuery) {
        if (JSON.stringify(newQuery) !== this.lastQueryParams) {
          this.lastQueryParams = JSON.stringify(newQuery)
          this.fetchBusinesses(newQuery)
        }
      },
      deep: true,
      immediate: true
    }
  },
  mounted() {
    const bottomBoundaryRef = this.$refs.bottomBoundaryRef;
    if (bottomBoundaryRef) {
      this.observer = new IntersectionObserver((entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          this.visibleCount += 5;
        }
      });
      this.observer.observe(bottomBoundaryRef);
    }
  },
  beforeDestroy() {
    if (this.observer) this.observer.disconnect();
  },
  methods: {
    async openBusiness(business) {
      this.loading = true
      if (this.$route.query.deep) {
        const businessId = await services.business.createAdvancedSearchBusiness(business)
        business.id = businessId
      }

      if (!business) {
        console.error('Business not found')
        return
      }

      try {
        const storeOrgId = this.$store?.getters?.['auth/orgId'] ?? null
        if (storeOrgId) {
          await this.fetchOrgUsage(storeOrgId)
        } else {
          await this.fetchCurrentUsage()
        }
      } catch (err) {
        console.error('Failed to fetch usage data:', err)
      }

      const inCycleIds = (this.usageData?.betascores?.inCycle ?? []).map(b => Number(b.id))
      const activeIds = (this.usageData?.betascores?.active ?? []).map(b => Number(b.id))
      const viewedIds = new Set([...inCycleIds, ...activeIds])

      const businessId = Number(business.id)

      if (businessId && viewedIds.has(businessId)) {
        this.$router.push(`/business/${businessId}`)
        return
      }

      this.loading = false
      this.confirmOpen = true
    },

    async onConfirmProceed() {
      if (!this.selectedBusiness) return;
      this.confirming = true;
      try {
        const business = this.selectedBusiness;
        const response = await axios.get(`/business/${business.id}/details`);

        if (response && response.business && response.business.id) {
          const businessIdFromResponse = response.business.id;
          const updatedBusiness = {
            ...business,
            businessId: businessIdFromResponse,
            businessPhoto: response.placeData?.business_photo?.photoUri
          };

          const storedSearches = JSON.parse(localStorage.getItem("recentSearches") || "[]");
          storedSearches.unshift(updatedBusiness);
          const uniqueSearchesMap = new Map();
          for (const item of storedSearches) {
            if (!uniqueSearchesMap.has(item.id)) {
              uniqueSearchesMap.set(item.id, item);
            }
          }
          const limitedUniqueSearches = Array.from(uniqueSearchesMap.values()).slice(0, 5);
          localStorage.setItem("recentSearches", JSON.stringify(limitedUniqueSearches));

          const cache = JSON.parse(localStorage.getItem("businessCache") || "{}");
          Object.keys(cache).forEach(key => {
            if (cache[key]?.businesses) {
              cache[key].businesses = cache[key].businesses.map(biz =>
                biz.id === business.id ? { ...biz, businessId: businessIdFromResponse } : biz
              );
            }
          });
          localStorage.setItem("businessCache", JSON.stringify(cache));

          await this.$router.push(`/business/${businessIdFromResponse}`);
        } else {
          console.error("Business creation/verification failed");
        }
      } catch (error) {
        console.error("Error in business creation/verification:", error);
        this.showErrorModal = true
      } finally {
        this.confirming = false;
        this.confirmOpen = false;
        this.selectedBusiness = null;
      }
    },

    async fetchBusinesses(params) {
      this.loading = true
      if (params.deep) {
        const { businesses, pagination } = await services.business.advanceSearch(params)
        this.businesses = businesses
        this.pagination = pagination
      } else {
        const { businesses, pagination } = await services.business.search(params)
        this.businesses = businesses
        this.pagination = pagination
      }
      this.loading = false
    },
  }

}
</script>

<style scoped lang="scss">
.search-results {
  .body {
    @include stack-space($space-large);
    padding: $space-xlarge 0px;

    @media (max-width: $tablet-breakpoint-min-width) {
      padding: $space-xlarge $space-medium;
    }

    .heading {
      @include stack-space($space-small);
    }
  }
}
</style>
